import { createFileRoute, Outlet, redirect } from '@tanstack/react-router'
import { z } from 'zod'
import { EntityQueries } from '@navigator/data'
import { EntityTypeQueries } from '@shared/data'
import { styled } from '@mui/material'
import { notProp } from '@portal/pkg-utils'
import { Suspense, useEffect, useState } from 'react'
import { EntityDrawer } from '@navigator/EntityPage/view/EntityDrawer'
import { CenteredSpinner } from '@portal/pkg-ui'
import { EntityActionDialogAdapter } from '@navigator/EntityPage/adapter/EntityActionDialogAdapter'
import { zodValidator } from '@tanstack/zod-adapter'
import { ScopedEnablementQueries, ScopeProvider } from '@portal/pkg-authorization'
import { useRecentlyViewed } from '@navigator/RecentlyViewed/useRecentlyViewed'

export const Route = createFileRoute('/_auth/entity/$entityId')({
  component: RouteComponent,
  params: zodValidator(
    z.object({
      entityId: z.coerce.number(),
      utm_source: z.enum(['breadcrumb']).optional(),
    }),
  ),
  beforeLoad: async ({ params: { entityId }, context: { queryClient, scopedEnablements } }) => {
    const entity = await queryClient.ensureQueryData(EntityQueries.entity(entityId))
    const entityTypes = await queryClient.ensureQueryData(EntityTypeQueries.userEntityTypes())
    const entityType = entityTypes.find((et) => et.id === entity.entityTypeId)

    // A miss means the entity's type is outside the caller's reach. View Entity is
    // checked separately by the grid and the subentity nav. Redirect rather than
    // throwing on a missing type while loading the route.
    if (!entityType) {
      throw redirect({
        to: '/403',
        replace: true,
      })
    }

    // Everything on this page is asked within the entity's type.
    const scope = { entityTypeId: entityType.id }

    // The type list says the type is reachable, so a missing entry means the enablements were
    // cached before the type existed or was brought into scope. Refresh once rather than fail
    // closed on a type the user may act on.
    if (!scopedEnablements.knows(scope)) {
      scopedEnablements = await queryClient.fetchQuery(ScopedEnablementQueries.scopedEnablements())
    }

    return { entity, entityType, scope, scopedEnablements }
  },
})

const Root = styled('div', {
  name: 'EntityPage',
  slot: 'Root',
})({
  display: 'flex',
})

interface ContentProps {
  open: boolean
}

const Content = styled('div', {
  name: 'EntityPage',
  slot: 'Content',
  shouldForwardProp: notProp('open'),
})<ContentProps>(({ theme: _theme, open }) => ({
  display: 'flex',
  flexDirection: 'column',
  // Adapt content width based on drawer open/closed widths.
  // This is assuming certain widths that are hardcoded in `ExpandableDrawer` which is not ideal.
  width: `calc(100vw - ${open ? 240 : 64}px)`,
  // This also sets the height based on top toolbar height, not ideal.
  height: 'calc(100vh - 40px)',
  overflowX: 'hidden',
}))

function RouteComponent() {
  const { entityType, entity, scope } = Route.useRouteContext()
  const { setRecentlyViewed } = useRecentlyViewed()
  const [drawerOpen, setDrawerOpen] = useState(true)

  useEffect(() => {
    if (entity) {
      setRecentlyViewed({
        id: entity.id,
        name: entity.shortName!,
        entityTypeId: entity.entityTypeId,
      })
    }
  }, [setRecentlyViewed, entity])

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen)
  }

  return (
    <ScopeProvider scope={scope}>
      <Root>
        <EntityDrawer
          entityType={entityType}
          open={drawerOpen}
          onDrawerToggle={handleDrawerToggle}
        />
        <Content open={drawerOpen}>
          <Suspense fallback={<CenteredSpinner />}>
            <Outlet />
          </Suspense>
        </Content>
      </Root>
      <EntityActionDialogAdapter />
    </ScopeProvider>
  )
}
